import { useRef } from "react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { Col, Row } from "antd";
import Colors from "assets/Colors";
import CustomButton from "components/common/Button";
import ReactToPrint from "react-to-print";
import useOrderItems from "graphql/useOrderItems";
import moment from "moment";
import useStores from "graphql/useStores";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useTranslation } from "react-i18next";
import DocumentPDF from "../DocumentPDF";
import logo from "../../../../assets/images/logo.png";
import s from "../styles";
import { useBusinessContext } from "../../../../BusinessApp";

function ContentBill({
  storeInfo,
  data,
  storeId,
  visible,
  setconfirmationModal,
}) {
  const { t } = useTranslation();
  const { stores /* , loading */ } = useStores();
  const { business, currencyFormat } = useBusinessContext();
  const store = stores.find((store) => store._id === data.storeId);

  const date = new Date(data?.creationDatetime);

  const amountReceived = parseFloat(data?.change) + parseFloat(data?.total);
  const paymentType =
    data?.payment === "cash"
      ? t("components.common.Bill.cash")
      : data?.payment === null
      ? t("components.common.Bill.storeCredit")
      : data?.payment === "refund_cash"
      ? t("components.common.Bill.refundCash")
      : data?.payment === "store_credit_create"
      ? t("components.common.Bill.storeCreditCreated")
      : data?.payment === "card"
      ? t("components.common.Bill.card")
      : t("components.common.Bill.others");

  const { orderItems } = useOrderItems(data);
  const componentRef = useRef();

  const printDocument = async (
    data,
    storeInfo,
    storeId,
    orderItems,
    fileName
  ) => {
    const bill = await pdf(
      <DocumentPDF
        store={store}
        nif={business?.idNumber}
        data={data}
        storeInfo={storeInfo}
        storeId={storeId}
        orderItems={orderItems}
        paymentType={paymentType}
        currencyFormat={currencyFormat}
        t={t}
      />
    ).toBlob();
    saveAs(bill, fileName);
    await setconfirmationModal(true);
  };
  const regex = /^[0-9]*$/;

  return (
    <>
      <s.BillContainer ref={componentRef}>
        <s.logoPrint>
          <img width={100} src={logo} alt="logo" />
        </s.logoPrint>
        <s.headerDrawer>
          <s.headerTitle>{store?.name}</s.headerTitle>
          {business?.idNumber && (
            <s.headerInfoCompany>{`NIF ${business?.idNumber}`}</s.headerInfoCompany>
          )}

          <s.headerInfoCompany>
            {store?.address1} {store?.zip && `,${store.zip}`}
          </s.headerInfoCompany>
          <s.headerInfoCompany>
            {store?.city}
            {store?.city && store?.country && ", "}
            {store?.country}
          </s.headerInfoCompany>
          <s.headerInfoCompany>
            {store?.phone && `${formatPhoneNumberIntl(store?.phone)}`}
          </s.headerInfoCompany>

          <s.headerInfoDescrition>
            {moment(date).format("dddd, LL, h:mm a")}
          </s.headerInfoDescrition>
        </s.headerDrawer>

        <s.headerDrawer style={{ textAlign: "center" }}>
          <s.divider />
          <s.subTitleBill>
            {data.type === "fac" &&
            (data.subtype === "complete" || data.subtype === "simple")
              ? t(`orders.types.${data.type}${data.subtype}`)
              : t(`orders.types.${data.type}`)}
          </s.subTitleBill>
          <s.divider />
        </s.headerDrawer>

        <section>
          <s.contentBill
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <s.pBill> {t("components.common.Bill.date")}</s.pBill>
            <s.pBill> {moment(date).format("dddd, LL, h:mm a")}</s.pBill>
          </s.contentBill>
          <s.contentBill
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <s.pBill>{t("components.common.Bill.terminal")}</s.pBill>
            <s.pBill>{data?.invoiceSerial}</s.pBill>
          </s.contentBill>
          <s.contentBill
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <s.pBill>{t("components.common.Bill.number")}</s.pBill>
            <s.pBill>
              {data.invoiceSerial}-{data.invoiceSequence}-{data.invoiceNumber}
            </s.pBill>
          </s.contentBill>
          <s.contentBill
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <s.pBill>{t("components.common.Bill.attendedBy")}</s.pBill>
            <s.pBill>{data?.openUserName}</s.pBill>
          </s.contentBill>
          {data.customerIdNumber && (
            <s.contentBill
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <s.pBill>{t("components.common.Bill.customerId")}</s.pBill>
              <s.pBill>{data.customerIdNumber}</s.pBill>
            </s.contentBill>
          )}
          <s.contentBill
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <s.pBill>{t("components.common.Bill.customer")}</s.pBill>
            {data?.customerName ? (
              <s.pBill>{data?.customerName}</s.pBill>
            ) : (
              <s.pBill>{t("components.common.Bill.customerName")}</s.pBill>
            )}
          </s.contentBill>
          <s.contentBill
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            {!!data?.customerAddress && (
              <>
                <s.pBill>{t("components.common.Bill.customerAddress")}</s.pBill>
                <s.pBill>
                  {data?.customerAddress && data?.customerAddress}
                  {data?.customerAddress2 && `, ${data?.customerAddress2}`}
                  {data?.customerCity && `, ${data?.customerCity}`}
                  {data?.customerZip && `, ${data?.customerZip}`}
                </s.pBill>
              </>
            )}
          </s.contentBill>

          <s.divider />

          <Row>
            <Col span={14}>
              <s.subTitleBill>
                {t("components.common.Bill.article")}
              </s.subTitleBill>
            </Col>
            <Col span={5}>
              <s.subTitleBill>
                {t("components.common.Bill.quantity")}
              </s.subTitleBill>
            </Col>
            <Col span={5} align="end">
              <s.subTitleBill>
                {t("components.common.Bill.price")}
              </s.subTitleBill>
            </Col>
          </Row>

          {orderItems?.map((item) => (
            <Row key={item._id}>
              <Col span={14}>
                <s.pBill>{item?.itemName}</s.pBill>
                {!regex.test(item?.barcode) && item?.barcode?.length > 0 && (
                  <s.pBilldetails>({item?.barcode})</s.pBilldetails>
                )}
              </Col>
              <Col span={5}>
                <s.pBill>{item?.quantity}</s.pBill>
              </Col>
              <Col span={5} align="end">
                <s.pBill>{currencyFormat(item?.baseUnitPrice)}</s.pBill>
              </Col>
            </Row>
          ))}

          <s.divider />

          <Row>
            <Col span={12}>
              <s.pBill>{t("components.common.Bill.subTotal")}</s.pBill>
            </Col>
            <Col span={12} align="end">
              <s.pBill>{data?.net && `${currencyFormat(data?.net)}`}</s.pBill>
            </Col>
          </Row>

          {data?.tax1Imp && (
            <Row>
              <Col span={12}>
                <s.pBill>{data?.tax1ImpName}</s.pBill>
              </Col>
              <Col span={12} align="end">
                <s.pBill>
                  {data?.tax1Imp && `${currencyFormat(data?.tax1Imp)}`}
                </s.pBill>
              </Col>
            </Row>
          )}

          <Row>
            <Col span={12}>
              <s.pBill>{data?.tax1Name}</s.pBill>
            </Col>

            <Col span={12} align="end">
              <s.pBill>
                {data?.tax1 && `${currencyFormat(data?.tax1)}`}{" "}
              </s.pBill>
            </Col>
          </Row>
          {/* *********** */}

          {data?.tax2ImpName && (
            <Row>
              <Col span={12}>
                <s.pBill>{data?.tax2ImpName}</s.pBill>
              </Col>
              <Col span={12} align="end">
                <s.pBill>
                  {data?.tax2ImpName && `${currencyFormat(data?.tax2Imp)}`}
                </s.pBill>
              </Col>
            </Row>
          )}
          {data?.tax2Name && (
            <Row>
              <Col span={12}>
                <s.pBill>{data?.tax2Name}</s.pBill>
              </Col>

              <Col span={12} align="end">
                <s.pBill>
                  {data?.tax2 && `${currencyFormat(data?.tax2)}`}{" "}
                </s.pBill>
              </Col>
            </Row>
          )}
          {/* * */}
          {data?.tax3ImpName && (
            <Row>
              <Col span={12}>
                <s.pBill>{data?.tax3ImpName}</s.pBill>
              </Col>
              <Col span={12} align="end">
                <s.pBill>
                  {data?.tax3ImpName && `${currencyFormat(data?.tax3Imp)}`}
                </s.pBill>
              </Col>
            </Row>
          )}
          {data?.tax3Name && (
            <Row>
              <Col span={12}>
                <s.pBill>{data?.tax3Name}</s.pBill>
              </Col>

              <Col span={12} align="end">
                <s.pBill>
                  {data?.tax3 && `${currencyFormat(data?.tax3)}`}{" "}
                </s.pBill>
              </Col>
            </Row>
          )}
          {/* * */}
          {/* * */}
          {data?.tax4ImpName && (
            <Row>
              <Col span={12}>
                <s.pBill>{data?.tax4ImpName}</s.pBill>
              </Col>
              <Col span={12} align="end">
                <s.pBill>
                  {data?.tax4ImpName && `${currencyFormat(data?.tax4Imp)}`}
                </s.pBill>
              </Col>
            </Row>
          )}
          {data?.tax4Name && (
            <Row>
              <Col span={12}>
                <s.pBill>{data?.tax4Name}</s.pBill>
              </Col>

              <Col span={12} align="end">
                <s.pBill>
                  {data?.tax4 && `${currencyFormat(data?.tax4)}`}{" "}
                </s.pBill>
              </Col>
            </Row>
          )}
          {/* * */}
          {/* ********* */}
          {data?.extraAmount1 && (
            <Row>
              <Col span={12}>
                <s.pBill> {t("components.common.Bill.tip")}</s.pBill>
              </Col>

              <Col span={12} align="end">
                <s.pBill>{currencyFormat(data?.extraAmount1)}</s.pBill>
              </Col>
            </Row>
          )}
          <Row>
            <Col span={12}>
              <s.subTitleBill>
                {t("components.common.Bill.total")}
              </s.subTitleBill>
            </Col>
            <Col span={12} align="end">
              <s.pBill>{currencyFormat(data?.total)} </s.pBill>
            </Col>
          </Row>

          <s.divider />

          <s.pBill>
            {t("components.common.Bill.paymentType")}: {paymentType}
          </s.pBill>
          <s.pBill>
            {t("components.common.Bill.received")}:{" "}
            {currencyFormat(amountReceived)}
          </s.pBill>
          {data.payment === "card" ? null : (
            <s.pBill>
              {t("components.common.Bill.change")}:{" "}
              {currencyFormat(data?.change)}
            </s.pBill>
          )}
        </section>
      </s.BillContainer>
      {visible && (
        <s.containerButton>
          <CustomButton
            style={{
              color: Colors.primary,
              width: "169px",
              fontWeight: "500",
              borderRadius: "5px",
              height: "40px",
              marginRight: "20px",
            }}
            onClick={() =>
              printDocument(
                data,
                storeInfo,
                storeId,
                orderItems,
                `${
                  data.type === "fac" &&
                  (data.subtype === "complete" || data.subtype === "simple")
                    ? t(`orders.types.${data.type}${data.subtype}`)
                    : t(`orders.types.${data.type}`)
                } #${data?.invoiceNumber}`
              )
            }
          >
            {t("components.common.Bill.download")}
          </CustomButton>

          <ReactToPrint
            trigger={() => (
              <CustomButton
                style={{
                  background: Colors.primary,
                  color: Colors.text_on_secondary,
                  width: "169px",
                  fontWeight: "500",
                  border: "none",
                  borderRadius: "5px",
                  height: "40px",
                }}
              >
                {t("components.common.Bill.print")}
              </CustomButton>
            )}
            content={() => componentRef.current}
          />
        </s.containerButton>
      )}
    </>
  );
}

export default ContentBill;
